/**
 * usePolygonLayers.ts
 * Hook for managing polygon visualizations on the map
 */

import { useDispatch } from "react-redux";

import { Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { DEFAULT_STEP } from "@/constants/dataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { updateDatasetStyle } from "@/store/slice/datasetSlice";
import { Dataset, DatasetAttributeSchema, DatasetStyle } from "@/types/dataset";
import { isOrdinalType } from "@/utils/dataset";
import { addLayer } from "@/utils/map";
import {
  addCentroidSource,
  addPolygonOrdinalLayers,
  addPolygonQuantileLayers,
  addPolygonSource
} from "@/utils/map/polygon";

type UsePolygonLayersProps = {
  map: Map;
  prepareLayerStyles: (
    parsedPolygonData: FeatureCollection<Geometry>,
    dataset: Dataset,
    selectedCategory: DatasetAttributeSchema,
    step: number
  ) => { updatedStyle: DatasetStyle };
  handleFeatureClick: (layerId: string) => void;
  handleMovePositionToLayer: (polygonData: FeatureCollection<Geometry>) => void;
  handleUpdateSourceWithRank: (value: {
    datasetId: string;
    style: DatasetStyle;
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    };
  }) => Promise<void>;
};

export const usePolygonLayers = ({
  map,
  prepareLayerStyles,
  handleFeatureClick,
  handleMovePositionToLayer,
  handleUpdateSourceWithRank
}: UsePolygonLayersProps) => {
  const dispatch = useDispatch();
  const { isNewUIDatasetConfigEnabled, isRankingLabelEnabled } =
    useAppFeatures();

  const handleAddPolygonLayer = (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>,
    customId?: string
  ) => {
    const sourceId = customId || dataset.data.id;
    if (!map) return;
    // Check if the dataset source already exists on the map
    if (map.getSource(sourceId)) return;

    const newLayer = {
      id: sourceId,
      type: dataset.properties.spatialType,
      sublayers: {
        fill: `${dataset.data.id}-fill`,
        outline: `${dataset.data.id}-outline`,
        outlineSelected: `${dataset.data.id}-outline-selected`,
        label: `${dataset.data.id}-label`,
        rank: `${dataset.data.id}-rank`
      }
    };

    const { updatedStyle } = prepareLayerStyles(
      polygonData,
      dataset,
      dataset.selectedCategory,
      dataset.style.colorSteps || DEFAULT_STEP
    );

    addPolygonSource(map, sourceId, polygonData);

    addCentroidSource(map, `${sourceId}-centroid`, polygonData);

    const isOrdinal = isOrdinalType(dataset.selectedCategory.data_type);

    addLayer({
      isNewUIDatasetConfigEnabled,
      isRankingLabelEnabled,
      map,
      layerData: newLayer,
      style: updatedStyle,
      selectedCategory: dataset.selectedCategory.attribute_name,
      isOrdinal,
      addOrdinalFunc: addPolygonOrdinalLayers,
      addQuantileFunc: addPolygonQuantileLayers
    });

    handleUpdateSourceWithRank({
      datasetId: `${sourceId}-centroid`,
      style: updatedStyle,
      update: {}
    });

    dispatch(
      updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
    );

    handleFeatureClick(newLayer.sublayers.fill);

    if (dataset.properties.isVisible) handleMovePositionToLayer(polygonData);
  };

  return {
    handleAddPolygonLayer
  };
};

export default usePolygonLayers;
